import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import type { WizardFormValues } from "../onboardingWizardForm";
import { professionGating } from "./preferencesValidation";
import FormSelect from "~/components/FormSelect";
import FormTextField from "~/components/FormTextField";
import SelectAProfessionDisclaimer from "~/components/SelectAProfessionDisclaimer";
import { useProfessions } from "~/hooks";

export const OT_DISCLAIMER =
	"In accordance with AOTA guidelines, to achieve satisfactory completion of all courses approved by the AOTA, you must reflect on all completed course materials and how they apply to your specific practice. If upon reflection you have any question(s), we advise you to leave your question(s) in the comment box at the bottom of the course. Questions are generally answered within 24 hours. Any question specific to the OT practice will be answered by a licensed OT with significant (10+ years) clinical experience.";

interface ProfessionFieldsProps {
	isDisclaimerAccepted: boolean;
	setIsDisclaimerAccepted: (value: boolean) => void;
}

const ProfessionFields = ({
	isDisclaimerAccepted,
	setIsDisclaimerAccepted,
}: ProfessionFieldsProps) => {
	const { professions, isLoadingProfessions } = useProfessions();
	const {
		register,
		watch,
		formState: { errors },
	} = useFormContext<WizardFormValues>();

	const primaryProfessionId = watch("primaryProfessionId");
	const secondaryProfessionId = watch("secondaryProfessionId");

	const { requiresCataMemberNumber, showDisclaimer } = professionGating(
		{ primaryProfessionId, secondaryProfessionId },
		professions,
	);

	useEffect(() => {
		if (!showDisclaimer) setIsDisclaimerAccepted(false);
	}, [showDisclaimer, setIsDisclaimerAccepted]);

	const professionsReady = !!professions?.length;
	const primaryOptions =
		professions?.map((p) => ({ label: p.title, value: p._id })) ?? [];
	const secondaryOptions =
		professions
			?.filter((p) => p._id !== primaryProfessionId)
			.map((p) => ({ label: p.title, value: p._id })) ?? [];

	return (
		<div className="flex flex-col gap-8">
			<div className="flex phablet:flex-row flex-col gap-4">
				<FormSelect
					label="Primary"
					name="primaryProfessionId"
					register={register}
					errors={errors}
					options={primaryOptions}
					placeholder={
						isLoadingProfessions || !professionsReady
							? "Loading professions…"
							: "Select a profession…"
					}
					isEmpty={!primaryProfessionId}
					disabled={isLoadingProfessions || !professionsReady}
					requiredOptions={{ required: "Select a primary profession" }}
				/>

				<FormSelect
					label="Secondary"
					name="secondaryProfessionId"
					register={register}
					errors={errors}
					options={secondaryOptions}
					placeholder="None"
					isOptional
					isEmpty={!secondaryProfessionId}
					disabled={
						!primaryProfessionId || isLoadingProfessions || !professionsReady
					}
				/>
			</div>

			{requiresCataMemberNumber && (
				<FormTextField
					name="cataMemberNumber"
					label="CATA Member Number"
					register={register}
					errors={errors}
					placeholder="Enter your CATA member number"
					className="w-full rounded-lg border px-3 py-2 shadow shadow-slate-100 dark:bg-slate-700 dark:shadow-slate-900"
					requiredOptions={{ required: "Required for CATA members" }}
				/>
			)}

			{showDisclaimer && (
				<SelectAProfessionDisclaimer
					disclaimer={OT_DISCLAIMER}
					isDisclaimerAccepted={isDisclaimerAccepted}
					setIsDisclaimerAccepted={setIsDisclaimerAccepted}
				/>
			)}
		</div>
	);
};

export default ProfessionFields;
